<template>
  <div class="algebraic-pro-viz">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$f({{ xVal }}) = ?$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 仿真区域 -->
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="380" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid meet">
           <defs>
              <filter id="text-glow">
                <feGaussianBlur stdDeviation="6" result="glow" />
                <feMerge><feMergeNode in="glow"/><feMergeNode in="SourceGraphic"/></feMerge>
              </filter>
           </defs>

           <!-- Grid -->
           <g stroke="rgba(241, 245, 249, 0.03)" stroke-width="1.5">
              <line v-for="i in 17" :key="'v'+i" :x1="i*50" y1="0" :x2="i*50" y2="600" />
              <line v-for="i in 13" :key="'h'+i" x1="0" :y1="i*50" x2="800" :y2="i*50" />
           </g>

           <!-- Expression display -->
           <text x="50" y="80" fill="#94a3b8" font-size="20" font-weight="bold">原代数式:</text>
           <g transform="translate(50, 200)">
              <text x="0" y="0" fill="#f8fafc" font-size="64" font-weight="900" filter="url(#text-glow)">
                 {{ a }}<tspan fill="#60a5fa">x</tspan>² 
                 <tspan :fill="b >= 0 ? '#10b981' : '#f87171'">{{ b >= 0 ? ' + ' : ' - ' }}{{ Math.abs(b) }}</tspan>
                 <tspan fill="#60a5fa">x</tspan>
                 <tspan :fill="c >= 0 ? '#10b981' : '#f87171'">{{ c >= 0 ? ' + ' : ' - ' }}{{ Math.abs(c) }}</tspan>
              </text>
           </g>

           <!-- Substitution Arrow -->
           <path d="M 50 250 Q 50 350 200 350 L 700 350" fill="none" stroke="rgba(148, 163, 184, 0.3)" stroke-width="3" stroke-dasharray="10,5" />
           <text x="710" y="360" fill="#60a5fa" font-size="22" font-weight="bold">代入 x = {{ xVal }}</text>

           <!-- Evaluated Result -->
           <g transform="translate(50, 500)">
              <text x="0" y="0" fill="#94a3b8" font-size="20" font-weight="bold">数值计算:</text>
              <text x="0" y="60" fill="#22d3ee" font-size="56" font-weight="900">
                 = {{ calculationResult.toFixed(2) }}
              </text>
           </g>
        </svg>

        <div class="hud-panel">
           <div class="hud-item blue">
              <label>自变量替换</label>
              <span class="val">x → {{ xVal }}</span>
           </div>
           <div class="hud-item cyan">
              <label>计算结果</label>
              <span class="val">{{ resultInt }}</span>
           </div>
        </div>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
        <div class="control-card">
          <div class="control-grid-mini">
             <div class="s-item">
                <label>修改系数 a ({{ a }})</label>
                <el-slider v-model="a" :min="-5" :max="5" size="small"></el-slider>
             </div>
             <div class="s-item">
                <label>修改自变量 x ({{ xVal }})</label>
                <el-slider v-model="xVal" :min="-10" :max="10" size="small"></el-slider>
             </div>
          </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <div class="discovery-content">
              <p><strong>代数本质：</strong>字母 <strong>x</strong> 只是一个“占位符”。求值过程就是将具体的数值<strong>填入</strong>占位符，并按照运算优先级（先乘方、后乘除、再加减）导出结果。</p>
           </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AlgebraicExpressionViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      a: 1,
      b: -4,
      c: 3,
      xVal: 5
    }
  },
  computed: {
    calculationResult() {
      return this.a * Math.pow(this.xVal, 2) + this.b * this.xVal + this.c;
    },
    resultInt() {
       const res = this.calculationResult;
       return Number.isInteger(res) ? res : res.toFixed(1);
    }
  }
}
</script>

<style scoped lang="scss">
.algebraic-pro-viz { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 380px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; .hud-panel { position: absolute; top: 12px; right: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #f8fafc; font-weight: 800; font-family: monospace; } &.blue { border-left: 3px solid #3b82f6; } &.cyan { border-left: 3px solid #10b981; } } } }
.compact-controls { flex: 0; display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; .s-item { label { font-size: 11px; color: #64748b; font-weight: 700; margin-bottom: 2px; } } }
.discovery-card-compact { background: #f0fdf4; padding: 10px 14px; border-radius: 10px; border: 1px solid #dcfce7; display: flex; align-items: center; gap: 10px; i { color: #16a34a; font-size: 1.1rem; } .discovery-content { flex: 1; p { margin: 0; font-size: 12px; color: #166534; line-height: 1.5; strong { color: #14532d; } } } }
</style>
